import { ErrorBoundary, type JSX } from "solid-js";
import { ErrorFallback } from "@/components/ui/error-fallback";

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** "page" fills the viewport (root shell); "region" fits inside a panel. */
  variant?: "page" | "region";
  /** Tag used to identify which boundary fired in console logs. */
  scope?: string;
  /** Optional custom copy for region boundaries. */
  title?: string;
  description?: string;
}

/**
 * Solid `<ErrorBoundary>` pre-wired with the premium {@link ErrorFallback}.
 *
 * Unlike TanStack Router's `errorComponent` (which only catches errors thrown
 * during route loading/rendering), this catches *render-time* errors anywhere
 * in the component subtree — so a crashing reader, Strong panel, or search
 * result never blanks the whole app. Each boundary can be reset independently.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(error: unknown, reset: () => void) => (
        <ErrorFallback
          error={error}
          reset={reset}
          variant={props.variant ?? "region"}
          scope={props.scope}
          title={props.title}
          description={props.description}
        />
      )}
    >
      {props.children}
    </ErrorBoundary>
  );
}
